import angular, { IScope } from "angular"
import _ from "lodash"
import IoT from "common-library/types/IoT"
import { Nullable } from "common-library/utils/types"
import {
  form2TrackerBody,
  resolveDetectionPointSelection,
  resolveTrackerSelection,
  sortDetectionPointOptions,
  trackerBody2Form,
  TrackingEngineFormOption,
} from "./tracking-engine-form.mapper"

/* 폼 field 타입: 경계(mapper)의 옵션 표현과 동일한 모양을 사용해 역매핑 결과를 직접 할당한다. */
type TrackingEngineForm = {
  detectionPoint?: TrackingEngineFormOption<IoT.Tracker.DetectionPoint>
  tracker?: TrackingEngineFormOption<IoT.TrackingEngine.Id>
}

const ENGINE_TR: { [k in IoT.TrackingEngine.Id]: string } = {
  dl_people_tracker: "DLPT",
  dl_skeleton_tracker: "DLST",
  dl_thermal_tracker: "DLTT",
  dl_fisheye_tracker: "DLFT",
  dl_object_tracker: "DLOT",
  object_tracker: "OT",
  unknown_tracker: "Unknown Tracker",
  rva_tracker: "Default",
}

type TrackingEngineFormDetectionPoint = {
  name: string
  value: IoT.Tracker.DetectionPoint
}

type TrackingEngineFormTrackingEngine = {
  name: string
  value: IoT.TrackingEngine.Id | null // `null`: "use sensor's setting" (미설정)
}

export type TrackingEngineFormData = Nullable<IoT.BackendTrackingConfig.VcaEdgeBody>

interface ControllerScope extends IScope {
  data?: TrackingEngineFormData
  deviceProperties?: IoT.Device.Description["properties"]
  licenses?: IoT.License.Data[]
  noLicenseValidation?: boolean
  availTrackers: IoT.TrackingEngine.Id[]
  backendKind?: IoT.FotaBackend
  edgeFaceGroupFace?: boolean
  isNew: boolean
  onChange: (data: TrackingEngineFormData) => void

  form: TrackingEngineForm
  detectionPoints: TrackingEngineFormDetectionPoint[]
  trackers: TrackingEngineFormTrackingEngine[]
  sensorsAsDefault?: boolean
  handleChange: () => void
  onTrackerChange: () => void
}

angular.module("uCountitUiApp").directive("trackingEngineForm", () => ({
  restrict: "E",
  templateUrl: "components/trackingengineconfig/TrackingEngineForm.html",
  scope: {
    data: "=",
    onChange: "=",
    licenses: "=",
    deviceProperties: "=",
    noLicenseValidation: "=",
    availTrackers: "=",
    isNew: "=",
    sensorsAsDefault: "=?",
    backendKind: "=?",
    edgeFaceGroupFace: "=?",
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  controller: ($scope: ControllerScope, Locale: any) => {
    let didSetDefault = false

    const resetDefaultAndUpdateForm = () => {
      didSetDefault = false
      if ($scope.data) {
        updateForm($scope.data)
      }
    }

    // Feature 변경으로 사용 가능한 TrackEngine이 바뀌면 새 센서의 기본값을 재설정
    $scope.$watchCollection("availTrackers", () => {
      if ($scope.isNew) {
        resetDefaultAndUpdateForm()
      } else if ($scope.data) {
        updateForm($scope.data)
      }
    })

    // 기존 센서 → 새 센서 전환 시 기본값 재설정
    $scope.$watch("isNew", (newVal, oldVal) => {
      if (newVal === true && oldVal !== true) {
        resetDefaultAndUpdateForm()
      }
    })

    // data, deviceProperties, licenses, noLicenseValidation 변경 시 폼 업데이트.
    // 데이터 기반 로드는 emit 하지 않는다(역방향 갱신은 사용자 입력 ng-change 전용).
    // 이로써 부모 echo → updateForm → emit 의 폐루프(사용자 입력 없이 dirty해지는 버그)가 차단된다.
    $scope.$watchGroup(["data", "deviceProperties", "licenses", "noLicenseValidation"], () => {
      if ($scope.data) {
        updateForm($scope.data)
      }
    })

    // 사용자가 EdgeFace/GroupFace를 체크 인/아웃할 때 트래커 옵션을 갱신하고,
    // 체크 인 시에는 one-shot으로 edgeFace tracker + 첫 Detection Point를 재선택한다.
    $scope.$watch("edgeFaceGroupFace", (now, prev) => {
      if (now === prev) return
      updateTrackerOptions()
      if (now) {
        if (applyEdgeFaceReselection()) {
          $scope.handleChange()
        }
      } else {
        // 체크 아웃 시 옵션만 복원하고 선택은 보존
        $scope.form.tracker = resolveTrackerSelection($scope.trackers, $scope.form?.tracker?.value)
        updateDpOptions()
      }
    })

    function updateForm(data: TrackingEngineFormData) {
      if (!$scope.form) {
        $scope.form = {}
      }

      updateTrackerOptions()

      if ($scope.isNew) {
        if (!didSetDefault) {
          $scope.form.tracker = $scope.trackers[0] ?? $scope.form.tracker
          updateDpOptions()
          // tracker의 dps 기반 첫 옵션(Bottom→Center 정렬)을 기본 detectionPoint로 지정한다.
          // 미설정 시 저장 단계에서 detectionPoint가 누락되어 "Unknown"으로 표시되는 것을 방지한다.
          $scope.form.detectionPoint = resolveDetectionPointSelection(
            $scope.detectionPoints,
            $scope.form?.detectionPoint?.value
          )
          if ($scope.edgeFaceGroupFace) {
            applyEdgeFaceReselection()
          }
          didSetDefault = true
          // 최초 1회만 부모로 emit 하여 신규 센서 저장 시 빈 body가 PUT 되는 것을 차단한다.
          // 이후 watchGroup에 의한 echo 재진입은 아래 else branch에서 옵션만 재매핑한다(폐루프 차단).
          $scope.onChange(form2data($scope.form))
        } else {
          // echo 재진입(부모 vcaConfig 갱신 → watchGroup → updateForm): 옵션만 재매핑하고 emit 안 함
          $scope.form.tracker =
            trackerBody2Form(
              { tracker: $scope.form.tracker?.value ?? undefined },
              { trackers: $scope.trackers, detectionPoints: [] }
            ).tracker ?? $scope.form.tracker
          updateDpOptions()
          if ($scope.edgeFaceGroupFace) {
            applyEdgeFaceReselection()
          }
        }
        return
      }

      // 기존 센서: body → form 역매핑. engine 결정 → dp 옵션 계산 → dp 결정 순서로
      // dp 옵션이 선택된 engine에 의존하므로 두 단계 역매핑한다.
      const engineMapped = trackerBody2Form(data, {
        trackers: $scope.trackers,
        detectionPoints: [],
      })
      $scope.form.tracker = engineMapped.tracker ?? $scope.form.tracker
      updateDpOptions()
      const dpMapped = trackerBody2Form(data, {
        trackers: [],
        detectionPoints: $scope.detectionPoints,
      })
      $scope.form.detectionPoint = dpMapped.detectionPoint ?? $scope.form.detectionPoint
      if ($scope.edgeFaceGroupFace) {
        applyEdgeFaceReselection()
      }
    }

    function updateTrackerOptions() {
      const deviceEngines = $scope.deviceProperties?.trackingEngines ?? []
      // EdgeFace/GroupFace 활성 시 edgeFace functionality를 가진 엔진만 허용
      const allowed = $scope.edgeFaceGroupFace
        ? deviceEngines.filter((te) => te.functionality.includes("edgeFace"))
        : deviceEngines

      let trackers: IoT.TrackingEngine.Id[] = []
      if ($scope.noLicenseValidation) {
        trackers = allowed.map((te) => te.id)
      } else {
        trackers = _.intersection(
          _.uniq($scope.licenses?.map((l) => l.trackingEngines).flat() ?? []),
          allowed.map((te) => te.id)
        )
      }

      $scope.trackers = trackers.map((v) => ({
        name: ENGINE_TR[v],
        value: v,
      })) as TrackingEngineFormTrackingEngine[]

      if ($scope.availTrackers && $scope.availTrackers.length > 0) {
        $scope.trackers = $scope.trackers.filter(
          (te) => te.value && $scope.availTrackers.includes(te.value)
        )
      }

      // EdgeFace/GroupFace 필터링 중에는 "use sensor's setting"(null)을 선택 불가로 둔다.
      // (미설정으로 저장 시 edgeFace 불가 tracker로 위임되는 것을 막기 위함)
      if (!$scope.edgeFaceGroupFace) {
        const sensorsSetting: TrackingEngineFormTrackingEngine = {
          name: Locale.string("t18n use sensor's setting"),
          value: null,
        }

        // selectable tracker가 정확히 하나뿐이면 새 센서의 기본값이 그 엔진이 되도록
        // sensorsSetting을 항상 뒤로 push한다. (license/availTrackers 필터 이후 기준)
        const singleEngineOnly = $scope.trackers.length === 1

        if ($scope.sensorsAsDefault && !singleEngineOnly) {
          $scope.trackers.unshift(sensorsSetting)
        } else {
          $scope.trackers.push(sensorsSetting)
        }
      }
    }

    function updateDpOptions() {
      const teId = $scope.form?.tracker?.value
      let dps: IoT.Tracker.DetectionPoint[] | undefined = undefined
      if (teId) {
        dps = $scope.deviceProperties?.trackingEngines.find((te) => te.id === teId)?.dps
      }
      if (teId && dps) {
        $scope.detectionPoints = sortDetectionPointOptions(
          dps.map((v) => ({
            name: Locale.string(v.charAt(0).toUpperCase() + v.slice(1)),
            value: v,
          }))
        ) as TrackingEngineFormDetectionPoint[]
      } else {
        // engine이 sensor-default(또는 미선택)이면 dp 옵션도 없다 → dp 필드도 미설정으로 둔다
        $scope.detectionPoints = []
      }
    }

    // EdgeFace/GroupFace 활성 시: 추적 엔진 선택을 보존하되 목록에 없으면 첫 번째 실제 엔진을 선택한다.
    // Detection Point는 현재 값이 해당 엔진 dps 목록에 유효하면 보존하고, 아니면 첫(Bottom→Center 정렬)로 폴백한다.
    // edgeFace 가능 엔진이 하나도 없으면 아무것도 변경하지 않는다(빈 설정 emit 방지).
    function applyEdgeFaceReselection() {
      if (!$scope.trackers.some((t) => t.value != null)) return false
      $scope.form.tracker = resolveTrackerSelection($scope.trackers, $scope.form?.tracker?.value)
      updateDpOptions()
      $scope.form.detectionPoint = resolveDetectionPointSelection(
        $scope.detectionPoints,
        $scope.form?.detectionPoint?.value
      )
      return true
    }

    function form2data(form: Partial<TrackingEngineForm>): TrackingEngineFormData {
      return form2TrackerBody(form)
    }

    $scope.handleChange = () => {
      $scope.onChange(form2data($scope.form))
    }

    // 사용자가 tracker를 바꾸면 dp 옵션을 즉시 갱신한다.
    // 새 engine의 dps 목록에 현재 detectionPoint가 없으면(호환되지 않거나 engine이
    // sensor-default/미선택이거나 dps가 아예 없는 경우 포함) 미설정(omit)으로 리셋한다.
    $scope.onTrackerChange = () => {
      updateDpOptions()
      $scope.form.detectionPoint = resolveDetectionPointSelection(
        $scope.detectionPoints,
        $scope.form?.detectionPoint?.value
      )
      $scope.handleChange()
    }
  },
}))
